import {assert} from 'Asserts';
import {SchemaInterfaceExtendingSchema, TagSchema} from 'constants/RegistryConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {
  isTagSchema,
  isBoxSchema,
  isContainerSchema,
  isLocationSchema,
  isPlateSchema,
  isSchemaInterface,
} from 'schemas/SchemaPredicates';

import {
  StructuredTableColumnSummaryRequirement,
  StructuredTableColumnSummary,
} from '../StructuredTableConstants';

/**
 * Computes the column summary of a column which contains items of the provided schema.
 *
 * @param schema The schema
 * @returns The column summary of a column which contains items of the provided schema
 */
export default function getColumnSummaryForSchema(schema: Schema): StructuredTableColumnSummary {
  const schemaPredicateAndGetColumnRequirements = SCHEMA_PREDICATE_AND_COLUMN_REQUIREMENTS_FN.find(
    ({isMatchingSchemaType}) => isMatchingSchemaType(schema)
  );
  assert(schemaPredicateAndGetColumnRequirements != null, `Unsupported schema ${schema.id}`);
  const {getColumnRequirements} = schemaPredicateAndGetColumnRequirements;
  return {
    validDestinationColumnRequirements: getColumnRequirements(schema),
    schemaIdConstraint: schema.id,
  };
}

const SCHEMA_PREDICATE_AND_COLUMN_REQUIREMENTS_FN = [
  {
    isMatchingSchemaType: (schema: Schema) => isTagSchema(schema),
    getColumnRequirements: (schema: Schema) => getColumnConstraintsForTagSchema(schema as TagSchema),
  },
  {
    isMatchingSchemaType: (schema: Schema) => isBoxSchema(schema),
    getColumnRequirements: (schema: Schema) =>
      getColumnConstraintsForStorableSchema(schema as BoxSchema, TableColumnType.BOX),
  },
  {
    isMatchingSchemaType: (schema: Schema) => isContainerSchema(schema),
    getColumnRequirements: (schema: Schema) =>
      getColumnConstraintsForStorableSchema(schema as ContainerSchema, TableColumnType.CONTAINERS),
  },
  {
    isMatchingSchemaType: (schema: Schema) => isLocationSchema(schema),
    getColumnRequirements: (schema: Schema) =>
      getColumnConstraintsForStorableSchema(schema as LocationSchema, TableColumnType.PARENT_STORABLE),
  },
  {
    isMatchingSchemaType: (schema: Schema) => isPlateSchema(schema),
    getColumnRequirements: (schema: Schema) =>
      getColumnConstraintsForStorableSchema(schema as PlateSchema, TableColumnType.PLATE),
  },
  {
    isMatchingSchemaType: (schema: Schema) => isSchemaInterface(schema),
    getColumnRequirements: (schema: Schema) =>
      getColumnConstraintsForSchemaInterface(schema as SchemaInterfaceExtendingSchema),
  },
];

function getColumnConstraintsForSchemaInterface(
  schemaInterface: SchemaInterfaceExtendingSchema
): Array<StructuredTableColumnSummaryRequirement> {
  const folderItemType = schemaInterface.baseBioentityType?.folderItemType ?? null;
  return [
    {
      columnType: TableColumnType.TAG_VALUE,
      schemaFieldType: FieldType.BIOENTITY_LINK,
      folderItemType,
    },
    {
      columnType: TableColumnType.EXISTING_BIOENTITY,
      schemaFieldType: null,
      folderItemType,
    },
  ];
}

function getColumnConstraintsForTagSchema(
  tagSchema: TagSchema
): Array<StructuredTableColumnSummaryRequirement> {
  const folderItemType = tagSchema.folderItemType;
  return [
    {
      columnType: TableColumnType.TAG_VALUE,
      schemaFieldType: FieldType.BIOENTITY_LINK,
      folderItemType,
    },
    {
      columnType: TableColumnType.EXISTING_BIOENTITY,
      schemaFieldType: null,
      folderItemType,
    },
  ];
}

function getColumnConstraintsForStorableSchema(
  storableSchema: StorableSchema,
  storableColumnType:
    | typeof TableColumnType.PLATE
    | typeof TableColumnType.BOX
    | typeof TableColumnType.CONTAINERS
    | typeof TableColumnType.PARENT_STORABLE
): Array<StructuredTableColumnSummaryRequirement> {
  return [
    {
      columnType: TableColumnType.TAG_VALUE,
      schemaFieldType: FieldType.STORABLE_LINK,
      folderItemType: null,
    },
    {
      columnType: storableColumnType,
      schemaFieldType: null,
      folderItemType: null,
    },
  ];
}
